import { useEffect, useState } from "react";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import PageHeader from "@/pages/_shared/PageHeader";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { FadeUp, Stagger, StaggerItem } from "@/pages/_shared/anim";

const phases = [
  {
    code: "01",
    title: "Share Your Requirement",
    duration: "You share",
    deliverable: "Requirement documented",
    desc: "Tell us about your elevator, escalator, spare part, installation requirement, or technical issue.",
    activities: [
      "Brand and model information",
      "Part number or component details",
      "Description of technical issue",
      "Photos or documentation",
      "Site or system details if applicable",
    ],
  },
  {
    code: "02",
    title: "Technical Assessment",
    duration: "We evaluate",
    deliverable: "Technical review",
    desc: "Our team reviews the available system information and identifies possible technical approaches.",
    activities: [
      "System information analysis",
      "Component compatibility check",
      "Technical feasibility review",
      "Alternative solution options",
      "Preliminary recommendations",
    ],
  },
  {
    code: "03",
    title: "Solution & Quotation",
    duration: "Proposal stage",
    deliverable: "Solution proposal",
    desc: "Receive the recommended part, service, repair, upgrade, or installation solution with commercial details.",
    activities: [
      "Recommended solution details",
      "Commercial quotation",
      "Timeline estimation",
      "Scope clarification",
      "Customer review and confirmation",
    ],
  },
  {
    code: "04",
    title: "Execution & Support",
    duration: "We deliver",
    deliverable: "Solution delivered",
    desc: "Depending on the requirement, we coordinate supply, repair, installation, programming, technical service, or commissioning.",
    activities: [
      "Part supply and logistics",
      "On-site or workshop service",
      "Installation coordination",
      "Programming and configuration",
      "Technical commissioning",
    ],
  },
  {
    code: "05",
    title: "Testing & Follow-Up",
    duration: "We verify",
    deliverable: "Solution complete",
    desc: "Where applicable, testing, commissioning, inspection, and technical follow-up help complete the solution.",
    activities: [
      "System testing and verification",
      "Operational commissioning",
      "Safety inspection where applicable",
      "Documentation handover",
      "Technical follow-up support",
    ],
  },
];

const capabilities = [
  { icon: "ri-cpu-line", label: "PCB Diagnostics & Repair" },
  { icon: "ri-test-tube-line", label: "Testing & Commissioning" },
  { icon: "ri-code-line", label: "Programming & Configuration" },
  { icon: "ri-survey-line", label: "System Condition Assessment" },
  { icon: "ri-shield-check-line", label: "Safety Inspection & Reporting" },
  { icon: "ri-tools-line", label: "Test & Programming Tools" },
];

const standards = [
  { icon: "ri-checkbox-circle-line", text: "Correct Technical Assessment" },
  { icon: "ri-stack-line", text: "Suitable Component Selection" },
  { icon: "ri-link", text: "System Compatibility" },
  { icon: "ri-settings-3-line", text: "Operational Reliability" },
  { icon: "ri-shield-check-line", text: "Safety-Focused Recommendations" },
  { icon: "ri-chat-check-line", text: "Clear Technical Communication" },
];

const faqs = [
  {
    q: "Do you provide spare parts for different elevator brands?",
    a: "Yes. Uplift One provides multi-brand elevator and escalator spare-parts solutions across a broad range of component categories.",
  },
  {
    q: "Can you help if I don't know the exact part number?",
    a: "Yes. Share the available brand, model, component details, or a clear photo so the requirement can be reviewed.",
  },
  {
    q: "Do you repair elevator PCBs?",
    a: "Uplift One provides all-brand PCB repair support covering single, double, and multilayer PCBs.",
  },
  {
    q: "Do you provide complete elevator installations?",
    a: "Yes. Supply and installation solutions include home, passenger, cargo, bed, panoramic, and kitchen elevators.",
  },
  {
    q: "Do you provide testing and commissioning?",
    a: "Yes. Technical support includes elevator and escalator testing and commissioning.",
  },
  {
    q: "Can you inspect an existing elevator system?",
    a: "Uplift One provides conditional surveys, technical advice, and safety inspection and reporting services.",
  },
  {
    q: "Do you provide alternative elevator designs?",
    a: "Yes. Alternative design and technical ideas can be considered for solutions with or without machine rooms.",
  },
];

export default function ProcessPage() {
  const [activePhase, setActivePhase] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setActivePhase((i) => (i + 1) % phases.length);
    }, 4000);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <div className="min-w-0 bg-crest-cream">
      <Navbar />
      <main>
        <PageHeader
          eyebrow="How we work"
          title={
            <>
              A Predictable Process
              <br />
              for{" "}
              <span className="text-white">Technical</span> Requirements.
            </>
          }
          description="From the first requirement to the final follow-up, we follow a structured five-phase process designed to match the technical complexity of elevator and escalator systems."
          crumbs={[
            { label: "Home", to: "/" },
            { label: "Process" },
          ]}
          meta={[
            { label: "Process phases", value: "5" },
            { label: "Assessment", value: "Technical" },
            { label: "Avg response", value: "1 day" },
            { label: "Coverage", value: "UAE" },
          ]}
        />

        {/* Process steps */}
        <section className="bg-crest-cream py-16 md:py-24">
          <div className="px-6 md:px-12">
            <div
              className="grid grid-cols-1 lg:grid-cols-12 gap-10"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
            >
              <div className="lg:col-span-12 space-y-6">
                {phases.map((p, idx) => {
                  const isActive = idx === activePhase;
                  return (
                    <FadeUp key={p.code} y={30}>
                      <motion.div
                        onMouseEnter={() => setActivePhase(idx)}
                        animate={{
                          borderColor: isActive
                            ? "rgb(234, 117, 38)"
                            : "rgba(0,0,0,0.05)",
                          boxShadow: isActive
                            ? "0 0 0 4px rgba(234, 117, 38, 0.18)"
                            : "0 0 0 0px rgba(0,0,0,0)",
                        }}
                        transition={{ duration: 0.4 }}
                        className="relative grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 rounded-xl border p-6 md:p-10 bg-white"
                      >
                        <div className="lg:col-span-3 flex lg:flex-col items-start gap-5">
                          <motion.div
                            animate={{
                              color: isActive
                                ? "rgb(234, 117, 38)"
                                : "rgba(26,45,66,0.2)",
                            }}
                            transition={{ duration: 0.3 }}
                            className="font-display text-5xl md:text-7xl lg:text-8xl leading-none"
                          >
                            {p.code}
                          </motion.div>
                          <div>
                            <div className="text-xs text-crest-deep/55 uppercase tracking-wider">
                              Phase {idx + 1}
                            </div>
                            <h3 className="font-display text-2xl text-crest-deep mt-1">
                              {p.title}
                            </h3>
                            <div className="flex items-center gap-2 mt-3 text-xs text-crest-deep/70">
                              <span className="w-2 h-2 rounded-full bg-crest-orange" />
                              <span className="font-semibold whitespace-nowrap">
                                {p.duration}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="lg:col-span-9 lg:border-l lg:border-black/5 lg:pl-10">
                          <p className="text-crest-deep/80 text-base md:text-lg leading-relaxed">
                            {p.desc}
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
                            {p.activities.map((a) => (
                              <div
                                key={a}
                                className="flex items-start gap-3 text-sm text-crest-deep/80"
                              >
                                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-crest-cream text-crest-deep shrink-0 mt-0.5 border border-black/5">
                                  <i className="ri-check-line text-xs"></i>
                                </span>
                                {a}
                              </div>
                            ))}
                          </div>
                          <div className="mt-6 pt-4 border-t border-black/5 flex items-center gap-3 text-xs text-crest-deep">
                            <i className="ri-file-text-line text-crest-bg"></i>
                            <span className="uppercase tracking-wider text-crest-deep/55">
                              Deliverable
                            </span>
                            <span className="font-semibold">{p.deliverable}</span>
                          </div>
                        </div>
                        <div className="absolute bottom-0 left-6 right-6 h-0.5 bg-transparent">
                          <motion.div
                            key={`pp-${idx}-${activePhase}-${paused}`}
                            initial={{ width: "0%" }}
                            animate={{
                              width: isActive && !paused ? "100%" : "0%",
                            }}
                            transition={{
                              duration: isActive && !paused ? 4 : 0.3,
                              ease: "linear",
                            }}
                            className="h-full bg-crest-orange"
                          />
                        </div>
                      </motion.div>
                    </FadeUp>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Technical Capabilities */}
        <section className="bg-crest-deep py-20 md:py-24">
          <div className="px-6 md:px-12">
            <FadeUp className="max-w-2xl mb-10">
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-crest-orange uppercase">
                <span className="w-6 h-px bg-crest-orange/60" /> Technical capabilities
              </span>
              <h2 className="font-display text-4xl md:text-5xl text-white leading-tight mt-5">
                The Technical Capabilities Behind Our Solutions.
              </h2>
            </FadeUp>
            <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" stagger={0.06}>
              {capabilities.map((c) => (
                <StaggerItem
                  key={c.label}
                  className="flex items-center gap-4 p-5 bg-crest-bg/30 border border-white/10 rounded-lg"
                >
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-crest-orange text-white shrink-0">
                    <i className={`${c.icon} text-xl`}></i>
                  </span>
                  <span className="text-white font-medium text-sm">
                    {c.label}
                  </span>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        {/* Standards */}
        <section className="bg-white py-20 md:py-24">
          <div className="px-6 md:px-12">
            <FadeUp className="max-w-2xl mb-10">
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-crest-deep/60 uppercase">
                <span className="w-6 h-px bg-crest-deep/40" /> Quality focus
              </span>
              <h2 className="font-display text-4xl md:text-5xl text-crest-deep leading-tight mt-5">
                The Standards We Focus On.
              </h2>
            </FadeUp>
            <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" stagger={0.06}>
              {standards.map((s) => (
                <StaggerItem
                  key={s.text}
                  className="flex items-center gap-4 p-5 bg-crest-cream rounded-lg border border-black/5"
                >
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-crest-deep text-crest-orange shrink-0">
                    <i className={`${s.icon} text-xl`}></i>
                  </span>
                  <span className="text-crest-deep font-medium text-sm">
                    {s.text}
                  </span>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-crest-cream py-20 md:py-24">
          <div className="px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <FadeUp className="lg:col-span-4">
                <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-crest-deep/60 uppercase">
                  <span className="w-6 h-px bg-crest-deep/40" /> Common questions
                </span>
                <h2 className="font-display text-4xl text-crest-deep leading-tight mt-5">
                  Common Questions. Clear Answers.
                </h2>
              </FadeUp>
              <Stagger className="lg:col-span-8 space-y-4" stagger={0.08}>
                {faqs.map((f) => (
                  <StaggerItem key={f.q}>
                    <details
                      className="group bg-white rounded-lg border border-black/5 px-6 py-5 cursor-pointer"
                    >
                      <summary className="flex items-center justify-between gap-4 list-none cursor-pointer">
                        <h4 className="font-display text-lg text-crest-deep">
                          {f.q}
                        </h4>
                        <span className="w-8 h-8 flex items-center justify-center rounded-full bg-crest-cream text-crest-deep group-open:bg-crest-deep group-open:text-crest-orange transition-colors">
                          <i className="ri-add-line group-open:hidden"></i>
                          <i className="ri-subtract-line hidden group-open:inline"></i>
                        </span>
                      </summary>
                      <p className="text-crest-deep/70 mt-4 leading-relaxed text-sm">
                        {f.a}
                      </p>
                    </details>
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-white py-16 md:py-20 border-t border-black/5">
          <FadeUp>
            <div className="px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <h3 className="font-display text-3xl md:text-4xl text-crest-deep">
                  Ready to share your requirement?
                </h3>
                <p className="text-crest-deep/70 mt-3 max-w-2xl">
                  Tell us about your elevator or escalator need — we&apos;ll begin the technical assessment and get back to you with a solution.
                </p>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-crest-orange text-white px-7 py-3.5 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-crest-orangeLight transition-colors"
              >
                Start your requirement
                <i className="ri-arrow-right-up-line"></i>
              </Link>
            </div>
          </FadeUp>
        </section>
      </main>
      <Footer />
    </div>
  );
}